// Screen-space battle HUD (GDD §7.1): the signature army roster strip (one card per
// owned squad with health, morale, upgrade pips, cover/posture state), point states +
// ticket counts top-center, resources, a production panel, and per-squad stance/ability
// controls. Drawn immediate-mode on the canvas; clickable areas are exposed as regions
// that input.ts hit-tests.

import { canAfford, canBuildUnit } from "../sim/economy";
import { STRUCTURES, TIER2_COST, unitsOf } from "../sim/data";
import type { Faction, Squad, UnitDef } from "../sim/types";
import type { World } from "../sim/world";
import { roundRectPath } from "./art";

export type RegionAction =
  | { kind: "roster"; squadId: number }
  | { kind: "build_unit"; unitId: string }
  | { kind: "build_struct"; structKind: "barracks" | "generator" | "turret" }
  | { kind: "build_tier" }
  | { kind: "build_upgrade"; squadId: number; upgradeId: string }
  | { kind: "fortify"; squadId: number }
  | { kind: "stance"; value: "aggressive" | "hold" | "retreat" }
  | { kind: "ability"; abilityId: string }
  | { kind: "cancel_queue"; index: number };

interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
  action: RegionAction;
  enabled: boolean;
}

export interface HudModel {
  playerFaction: Faction;
  selected: Set<number>;
  vw: number;
  vh: number;
  animTime: number;
  message: string;
}

const FACT_COL: Record<Faction, string> = { legion: "#b23040", kindled: "#e0771b" };

export class Hud {
  private ctx: CanvasRenderingContext2D;
  regions: Region[] = [];
  rosterHeight = 104;
  panelWidth = 224;

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  regionAt(x: number, y: number): Region | null {
    for (let i = this.regions.length - 1; i >= 0; i--) {
      const r = this.regions[i];
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return r;
    }
    return null;
  }

  /** True if a screen point is over any HUD chrome (so world clicks are ignored). */
  overChrome(x: number, y: number, vw: number, vh: number): boolean {
    if (this.regionAt(x, y)) return true;
    if (y > vh - this.rosterHeight) return true; // roster strip
    if (x > vw - this.panelWidth) return true; // production panel
    if (y < 64 && x > vw / 2 - 260 && x < vw / 2 + 260) return true; // top bar
    if (y < 78 && x < 230) return true; // resources
    return false;
  }

  draw(world: World, ui: HudModel): void {
    this.regions = [];
    this.drawTopBar(world, ui);
    this.drawResources(world, ui);
    this.drawRoster(world, ui);
    this.drawPanel(world, ui);
    if (ui.message) this.drawMessage(ui);
    this.drawHints(ui);
  }

  // ---- top bar: tickets + points ------------------------------------------

  private drawTopBar(world: World, ui: HudModel): void {
    const ctx = this.ctx;
    const cx = ui.vw / 2;
    const w = 520;
    panel(ctx, cx - w / 2, 8, w, 50, 0.82);

    const tally = world.pointTally();
    // point pips row (center)
    const n = world.points.length;
    const pipW = 26;
    const startX = cx - (n * pipW) / 2;
    world.points.forEach((p, i) => {
      const px = startX + i * pipW + pipW / 2;
      ctx.fillStyle = p.owner === "neutral" ? "#8a8577" : FACT_COL[p.owner as Faction];
      ctx.beginPath();
      ctx.arc(px, 22, 8, 0, Math.PI * 2);
      ctx.fill();
      if (p.contested) {
        ctx.strokeStyle = "#ffd24a";
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      if (p.isPower) {
        ctx.fillStyle = "#7fd0ff";
        ctx.beginPath();
        ctx.arc(px, 22, 3, 0, Math.PI * 2);
        ctx.fill();
      }
    });

    // ticket bars (Legion left, Kindled right)
    this.ticketBar(cx - w / 2 + 12, 36, 180, "legion", world, tally.legion);
    this.ticketBar(cx + w / 2 - 12 - 180, 36, 180, "kindled", world, tally.kindled);

    // timer
    ctx.fillStyle = "#cfc7ba";
    ctx.font = "600 13px Trebuchet MS";
    ctx.textAlign = "center";
    const mm = Math.floor(world.time / 60);
    const ss = Math.floor(world.time % 60);
    ctx.fillText(`${mm}:${ss.toString().padStart(2, "0")}`, cx, 50);
    ctx.textAlign = "left";
  }

  private ticketBar(x: number, y: number, w: number, f: Faction, world: World, pts: number): void {
    const ctx = this.ctx;
    const army = world.armies[f];
    const frac = army.ticket / world.cfg.ticketStart;
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(x, y, w, 12);
    ctx.fillStyle = FACT_COL[f];
    const bw = w * Math.max(0, Math.min(1, frac));
    ctx.fillRect(f === "legion" ? x : x + w - bw, y, bw, 12);
    ctx.fillStyle = "#efe7d9";
    ctx.font = "700 11px Trebuchet MS";
    ctx.textAlign = f === "legion" ? "left" : "right";
    ctx.fillText(`${f === "legion" ? "LEGION" : "KINDLED"}  ${Math.ceil(army.ticket)}  ◆${pts}`, f === "legion" ? x + 2 : x + w - 2, y + 10);
    ctx.textAlign = "left";
  }

  // ---- resources -----------------------------------------------------------

  private drawResources(world: World, ui: HudModel): void {
    const ctx = this.ctx;
    const army = world.armies[ui.playerFaction];
    panel(ctx, 8, 8, 214, 62, 0.82);
    ctx.font = "700 15px Trebuchet MS";
    ctx.fillStyle = "#f2c94c";
    ctx.fillText(`REQ ${Math.floor(army.req)}`, 18, 30);
    ctx.fillStyle = "#7fd0ff";
    ctx.fillText(`PWR ${Math.floor(army.pwr)}`, 118, 30);
    ctx.font = "500 11px Trebuchet MS";
    ctx.fillStyle = "#b8b0a2";
    ctx.fillText(`+${army.reqRate.toFixed(1)}/s`, 18, 46);
    ctx.fillText(`+${army.pwrRate.toFixed(1)}/s`, 118, 46);
    ctx.fillStyle = "#cfc7ba";
    const used = world.slotsUsed(ui.playerFaction);
    ctx.fillText(`Army ${used}/${army.slotCap} slots   HQ T${army.hqTier}`, 18, 61);
    if (ui.playerFaction === "kindled") {
      ctx.fillStyle = "#e0771b";
      ctx.fillText(`Fervor T${army.fervorTier}`, 150, 61);
    } else {
      ctx.fillStyle = "#b9c2cc";
      ctx.fillText(`Doctrine`, 158, 61);
    }
  }

  // ---- army roster strip ---------------------------------------------------

  private drawRoster(world: World, ui: HudModel): void {
    const ctx = this.ctx;
    const y0 = ui.vh - this.rosterHeight;
    panel(ctx, 0, y0, ui.vw - this.panelWidth, this.rosterHeight, 0.86);
    const squads = world.aliveSquads(ui.playerFaction).sort((a, b) => a.id - b.id);
    const cardW = 96;
    const cardH = 84;
    const pad = 8;
    let x = pad;
    const y = y0 + 10;
    for (const sq of squads) {
      if (x + cardW > ui.vw - this.panelWidth) break;
      this.rosterCard(sq, x, y, cardW, cardH, ui);
      x += cardW + 6;
    }
    ctx.fillStyle = "#8a8272";
    ctx.font = "600 11px Trebuchet MS";
    ctx.fillText("ARMY ROSTER — click select · dbl-click center · 1-9 groups", 10, y0 + this.rosterHeight - 6);
  }

  private rosterCard(sq: Squad, x: number, y: number, w: number, h: number, ui: HudModel): void {
    const ctx = this.ctx;
    const sel = ui.selected.has(sq.id);
    ctx.fillStyle = sel ? "rgba(60,72,50,0.95)" : "rgba(26,24,20,0.95)";
    roundRectPath(ctx, x, y, w, h, 6);
    ctx.fill();
    ctx.strokeStyle = sel ? "#8fd07a" : "rgba(90,84,72,0.8)";
    ctx.lineWidth = sel ? 2 : 1;
    ctx.stroke();

    // name
    ctx.fillStyle = "#e8e0d4";
    ctx.font = "700 11px Trebuchet MS";
    ctx.fillText(trunc(sq.def.name, 15), x + 7, y + 15);

    // health + morale bars
    const alive = sq.models.filter((m) => m.alive).length;
    let hp = 0;
    let hpMax = 0;
    for (const m of sq.models) if (m.alive) { hp += m.hp; hpMax += m.maxHp; }
    barRow(ctx, x + 7, y + 22, w - 14, hpMax ? hp / hpMax : 0, "#6fcf6f", "rgba(0,0,0,0.5)");
    const mc = sq.broken ? "#d0483c" : "#e0b83c";
    barRow(ctx, x + 7, y + 30, w - 14, sq.morale / sq.maxMorale, mc, "rgba(0,0,0,0.5)");

    // model pips
    for (let i = 0; i < sq.def.modelCount; i++) {
      ctx.fillStyle = i < alive ? FACT_COL[sq.faction] : "rgba(90,84,72,0.6)";
      ctx.fillRect(x + 7 + i * 8, y + 38, 6, 5);
    }

    // upgrade pips
    let ux = x + 7;
    if (sq.hasSergeant) { badge(ctx, ux, y + 48, "SGT", "#d4a017"); ux += 30; }
    if (sq.launcherModels > 0) { badge(ctx, ux, y + 48, "WPN", "#8fd8ff"); ux += 30; }

    // state line
    let state = "Holding";
    let scol = "#b8b0a2";
    if (sq.frenzy) { state = "FRENZY"; scol = "#ff8a2c"; }
    else if (sq.broken) { state = "BROKEN"; scol = "#ff6a5a"; }
    else if (sq.suppression >= 0.72) { state = "PINNED"; scol = "#cfcfe0"; }
    else if (sq.meleeLock.size) { state = "MELEE"; scol = "#ffd24a"; }
    else if (sq.models.some((m) => m.alive && m.posture === "incover")) { state = "In Cover"; scol = "#8fd8ff"; }
    else if (sq.discipline > 0.5) { state = "Dug In"; scol = "#b9c2cc"; }
    else if (sq.order.type === "attack" || sq.order.type === "attackmove") { state = "Attacking"; scol = "#e0a060"; }
    else if (sq.path.length) { state = "Moving"; scol = "#cfc7ba"; }
    ctx.fillStyle = scol;
    ctx.font = "600 10px Trebuchet MS";
    ctx.fillText(state, x + 7, y + h - 6);

    this.regions.push({ x, y, w, h, action: { kind: "roster", squadId: sq.id }, enabled: true });
  }

  // ---- production / command panel ------------------------------------------

  private drawPanel(world: World, ui: HudModel): void {
    const ctx = this.ctx;
    const px = ui.vw - this.panelWidth;
    panel(ctx, px, 76, this.panelWidth, ui.vh - 76, 0.88);
    let y = 88;
    const army = world.armies[ui.playerFaction];

    // selected squad command block
    const first = [...ui.selected].map((id) => world.squads.find((s) => s.id === id && !s.dead)).find(Boolean) as Squad | undefined;
    if (first && first.faction === ui.playerFaction) {
      ctx.fillStyle = "#e8e0d4";
      ctx.font = "700 13px Trebuchet MS";
      ctx.fillText(trunc(first.def.name, 22), px + 12, y);
      y += 8;
      // stances
      y += 14;
      const stances: ("aggressive" | "hold" | "retreat")[] = ["aggressive", "hold", "retreat"];
      const sw = (this.panelWidth - 32) / 3;
      stances.forEach((st, i) => {
        this.button(px + 12 + i * (sw + 4), y, sw, 24, st === "aggressive" ? "Aggr" : st === "hold" ? "Hold" : "Retr", "", true, first.stance === st, { kind: "stance", value: st });
      });
      y += 32;
      // hero abilities
      if (first.def.abilities) {
        for (const ab of first.def.abilities) {
          const cd = first.abilityCd[ab.id] ?? 0;
          this.button(px + 12, y, this.panelWidth - 24, 24, ab.name, cd > 0 ? `${Math.ceil(cd)}s` : "READY", cd <= 0, false, { kind: "ability", abilityId: ab.id });
          y += 28;
        }
      }
      // upgrades
      if (first.def.upgrades) {
        for (const up of first.def.upgrades) {
          const have = first.upgrades.includes(up.id);
          const ok = !have && canAfford(army, up.cost) && army.hqTier >= 1;
          this.button(px + 12, y, this.panelWidth - 24, 24, up.name, have ? "DONE" : `${up.cost.req}R`, ok, have, { kind: "build_upgrade", squadId: first.id, upgradeId: up.id });
          y += 28;
        }
      }
      // fortify (if standing on own point)
      const onPoint = world.points.findIndex((p) => p.owner === ui.playerFaction && !p.hasOutpost && Math.hypot(p.pos.x - first.center.x, p.pos.y - first.center.y) < p.captureRadius);
      if (onPoint >= 0) {
        this.button(px + 12, y, this.panelWidth - 24, 24, "Fortify Point", "80R", army.req >= 80, false, { kind: "fortify", squadId: first.id });
        y += 28;
      }
      y += 8;
      ctx.strokeStyle = "rgba(90,84,72,0.5)";
      ctx.beginPath();
      ctx.moveTo(px + 10, y);
      ctx.lineTo(px + this.panelWidth - 10, y);
      ctx.stroke();
      y += 12;
    }

    // BUILD header + queue
    ctx.fillStyle = "#cfc7ba";
    ctx.font = "700 12px Trebuchet MS";
    ctx.fillText("PRODUCTION", px + 12, y);
    y += 8;
    // queue chips
    y += 8;
    if (army.queue.length) {
      let qx = px + 12;
      army.queue.slice(0, 5).forEach((it, i) => {
        const frac = 1 - it.timeLeft / it.totalTime;
        ctx.fillStyle = "rgba(50,46,40,0.9)";
        roundRectPath(ctx, qx, y, 36, 20, 3);
        ctx.fill();
        ctx.fillStyle = "rgba(143,208,122,0.5)";
        ctx.fillRect(qx, y + 17, 36 * frac, 3);
        ctx.fillStyle = "#d8d0c2";
        ctx.font = "600 9px Trebuchet MS";
        ctx.fillText(abbrev(it.label), qx + 3, y + 13);
        this.regions.push({ x: qx, y, w: 36, h: 20, action: { kind: "cancel_queue", index: i }, enabled: true });
        qx += 40;
      });
      y += 28;
    } else {
      y += 6;
    }

    // unit buttons
    const units = unitsOf(ui.playerFaction);
    const bw = (this.panelWidth - 32) / 2;
    let col = 0;
    let rowY = y;
    for (const u of units) {
      const check = canBuildUnit(world, ui.playerFaction, u.id);
      const bx = px + 12 + col * (bw + 8);
      const label = u.role === "hero" ? u.name.split(" ")[0] : shortName(u);
      const cost = `${u.cost.req}R${u.cost.pwr ? `/${u.cost.pwr}P` : ""}`;
      this.button(bx, rowY, bw, 30, label, cost, check.ok, false, { kind: "build_unit", unitId: u.id });
      col++;
      if (col === 2) { col = 0; rowY += 34; }
    }
    if (col === 1) rowY += 34;
    y = rowY + 6;

    // structures + tier
    ctx.fillStyle = "#8a8272";
    ctx.font = "600 11px Trebuchet MS";
    ctx.fillText("STRUCTURES", px + 12, y);
    y += 8;
    const structs: ("barracks" | "generator" | "turret")[] = ["generator", "barracks", "turret"];
    col = 0;
    rowY = y + 4;
    for (const sk of structs) {
      const sd = STRUCTURES[sk];
      const bx = px + 12 + col * (bw + 8);
      const ok = canAfford(army, sd.cost) && !(sk === "generator" && army.usedGeneratorSlots >= army.generatorSlots.length);
      this.button(bx, rowY, bw, 28, sd.name, `${sd.cost.req}R${sd.cost.pwr ? `/${sd.cost.pwr}P` : ""}`, ok, false, { kind: "build_struct", structKind: sk });
      col++;
      if (col === 2) { col = 0; rowY += 32; }
    }
    if (col === 1) rowY += 32;
    y = rowY + 6;
    if (army.hqTier < 2) {
      this.button(px + 12, y, this.panelWidth - 24, 26, "Advance HQ → T2", `${TIER2_COST.req}R/${TIER2_COST.pwr}P`, canAfford(army, TIER2_COST) && !army.queue.some((q) => q.kind === "tier"), false, { kind: "build_tier" });
    }
  }

  private button(x: number, y: number, w: number, h: number, label: string, sub: string, enabled: boolean, active: boolean, action: RegionAction): void {
    const ctx = this.ctx;
    ctx.fillStyle = active ? "rgba(70,86,58,0.95)" : enabled ? "rgba(48,44,38,0.95)" : "rgba(30,28,24,0.9)";
    roundRectPath(ctx, x, y, w, h, 4);
    ctx.fill();
    ctx.strokeStyle = active ? "#8fd07a" : enabled ? "rgba(110,102,88,0.8)" : "rgba(60,56,48,0.6)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = enabled ? "#e8e0d4" : "#6a6458";
    ctx.font = "700 11px Trebuchet MS";
    ctx.fillText(trunc(label, Math.floor(w / 7)), x + 6, y + (sub ? 14 : h / 2 + 4));
    if (sub) {
      ctx.fillStyle = enabled ? "#a89f8e" : "#5a5448";
      ctx.font = "600 9px Trebuchet MS";
      ctx.fillText(sub, x + 6, y + h - 5);
    }
    this.regions.push({ x, y, w, h, action, enabled });
  }

  private drawMessage(ui: HudModel): void {
    const ctx = this.ctx;
    ctx.fillStyle = "rgba(0,0,0,0.7)";
    const w = ctx.measureText(ui.message).width + 40;
    roundRectPath(ctx, ui.vw / 2 - w / 2, 66, w, 26, 6);
    ctx.fill();
    ctx.fillStyle = "#ffd88a";
    ctx.font = "700 13px Trebuchet MS";
    ctx.textAlign = "center";
    ctx.fillText(ui.message, ui.vw / 2, 84);
    ctx.textAlign = "left";
  }

  private drawHints(ui: HudModel): void {
    const ctx = this.ctx;
    ctx.fillStyle = "rgba(150,142,128,0.7)";
    ctx.font = "500 10px Trebuchet MS";
    ctx.fillText("RClick move/attack · A attack-move · S stop · H hold · drag=select · drag order=facing · Q/W/E hero · [ ] speed", 10, ui.vh - this.rosterHeight - 8);
  }
}

// ---- helpers ---------------------------------------------------------------

function panel(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, alpha: number): void {
  ctx.fillStyle = `rgba(18,16,13,${alpha})`;
  roundRectPath(ctx, x, y, w, h, 8);
  ctx.fill();
  ctx.strokeStyle = "rgba(90,84,72,0.6)";
  ctx.lineWidth = 1;
  ctx.stroke();
}

function barRow(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, frac: number, color: string, bg: string): void {
  ctx.fillStyle = bg;
  ctx.fillRect(x, y, w, 5);
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w * Math.max(0, Math.min(1, frac)), 5);
}

function badge(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, color: string): void {
  ctx.fillStyle = color;
  roundRectPath(ctx, x, y, 26, 10, 2);
  ctx.fill();
  ctx.fillStyle = "#14110d";
  ctx.font = "700 8px Trebuchet MS";
  ctx.fillText(text, x + 3, y + 8);
}

function trunc(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function shortName(u: UnitDef): string {
  const map: Record<string, string> = {
    legion_line: "Line Inf", legion_heavy: "Heavy Wpn", legion_assault: "Assault", legion_vehicle: "Tank",
    kindled_zealot: "Zealots", kindled_firebearer: "Fire-bear", kindled_shock: "Shock", kindled_vehicle: "Buggy",
  };
  return map[u.id] ?? u.name;
}

function abbrev(s: string): string {
  return s.replace(/[^A-Za-z0-9]/g, "").slice(0, 4).toUpperCase();
}
